import React from 'react';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';

import { Form } from 'react-bootstrap';
import { Checkbox } from 'pretty-checkbox-react';
import EnergyTracerMultiLineChart, { createDownloadHeadersAndKeys, SELECTION_KEY, SELECTION_BASELINE_KEY, SELECTION_BASELINE2_KEY } from '../../components/EnergyTracerMultiLineChart';
import GraphActionContainer from '../../components/chartElements/GraphActionContainer';
import ChartToggle from '../../components/chartElements/Actions/ChartToggle';
import ChartDownload from '../../components/chartElements/Actions/ChartDownload';
import {
  ghg_units,
  ghg_conversions,
} from '../../helpers/GHGUnits';

import 'pretty-checkbox';

const row = {
  marginRight: '40px',
  paddingLeft: '1em',
  marginTop: '1em',
};

function UtilityBreakdown(props) {
  const [deferredProps, setDeferredProps] = useState({ ...props });
  useEffect(() => {
    if (props.open) setDeferredProps({ ...props });
  }, [props]);
  const {
    dashboardData,
    baselineData,
    baseline2Data,
    showBaselineData,
    showBaseline2Data,
    organization,
    activeIds,
    activeGHGLicense,
  } = deferredProps;

  const [availableUtilities, setAvailableUtilities] = useState([]);
  const [selectedUtilities, setSelectedUtilities] = useState([]);
  const [usageSelectedUtilities, setUsageSelectedUtilities] = useState([]);

  const [availableOtherTypes, setAvailableOtherTypes] = useState([]);
  const [selectedOtherTypes, setSelectedOtherTypes] = useState([]);

  const [costData, setCostData] = useState([]);
  const [usageData, setUsageData] = useState([]);
  const [GHGData, setGHGData] = useState([]);
  const [convertedGHGData, setConvertedGHGData] = useState([]);

  const [costSummary, setCostSummary] = useState(false);
  const [usageSummary, setUsageSummary] = useState(false);
  const [GHGSummary, setGHGSummary] = useState(false);


  const [chartDownloadHeaders, setChartDownloadHeaders] = useState([]);
  const [chartDownloadKeys, setChartDownloadKeys] = useState([]);
  const [usageChartDownloadHeaders, setUsageChartDownloadHeaders] = useState([]);
  const [usageChartDownloadKeys, setUsageChartDownloadKeys] = useState([]);

  const [activeGHGUnit, setActiveGHGUnit] = useState('lbs');
  const [GHGUnitFactor, setGHGUnitFactor] = useState(1);


  useEffect(() => {
    if (Object.keys(dashboardData).length) {
      let chart_utilities = dashboardData['available_utilities'].map((utility) => (utility.charAt(0).toUpperCase() +
        utility.substr(1).toLowerCase()));
      setAvailableUtilities(chart_utilities);
      setSelectedUtilities(chart_utilities);
    }
  }, [
    dashboardData
  ])

  useEffect(() => {
    let found_other_types = [];
    Object.keys(dashboardData['building_usage']).forEach((bid) => {
      if (dashboardData['building_usage'][bid]['other']) {
        Object.keys(dashboardData['building_usage'][bid]['other']['sub_costs']).forEach((other_type) => {
          if (!found_other_types.includes(other_type)) {
            found_other_types.push(other_type)
          }
        })
      }
    }
    )
    setAvailableOtherTypes(found_other_types);
    setSelectedOtherTypes(found_other_types);
  }, [dashboardData])

  useEffect(() => {
    // usage selected utilities (usage and ghg ignore water)
    let usage_selected_utilities = []
    selectedUtilities.forEach((utility) => {
      if (['usage', 'ghg'].includes('usage') && ['Electric', "Gas", "Propane"].includes(utility)) {
        usage_selected_utilities.push(utility)
      }
    })
    setUsageSelectedUtilities(usage_selected_utilities);

    const hasBaseline = Object.keys(baselineData).length > 0;
    const hasBaseline2 = Object.keys(baseline2Data).length > 0;

    createDownloadHeadersAndKeys(
      selectedUtilities, setChartDownloadHeaders, setChartDownloadKeys, hasBaseline, hasBaseline2
    )

    createDownloadHeadersAndKeys(
      usage_selected_utilities, setUsageChartDownloadHeaders, setUsageChartDownloadKeys, hasBaseline, hasBaseline2
    )

  }, [selectedUtilities, baselineData, baseline2Data])

  const changeCheck = (check) => {
    let new_checks = [...selectedUtilities];
    if (new_checks.includes(check)) {
      new_checks = new_checks.filter(function (item) {
        return item !== check;
      });
    } else {
      new_checks.push(check);
    }
    new_checks = new_checks.sort()
    setSelectedUtilities(new_checks);
  };

  const changeOtherCheck = (check) => {
    let new_checks = [...selectedOtherTypes];
    if (new_checks.includes(check)) {
      new_checks = new_checks.filter(function (item) {
        return item !== check;
      });
    } else {
      new_checks.push(check);
    }
    new_checks = new_checks.sort()
    setSelectedOtherTypes(new_checks);
  };



  useEffect(() => {
    setGHGUnitFactor(1 / ghg_conversions[activeGHGUnit]);
  }, [activeGHGUnit]);

  useEffect(() => {
    let altered_ghg_data = [];
    GHGData.forEach((month) => {
      let temp_month = {};
      Object.keys(month).forEach((key) => {
        if (['name', 'month'].includes(key)) {
          temp_month[key] = month[key];
        } else {
          temp_month[key] = month[key] * GHGUnitFactor;
        }
      });
      altered_ghg_data.push(temp_month);
    });
    setConvertedGHGData(altered_ghg_data);
  }, [GHGData, GHGUnitFactor]);




  const convertToChartForm = useCallback((dashboardData, baselineData, baseline2Data, selectedUtilities, chartType, activeIds, selectedOtherTypes) => {
    const hasBaseline = Object.keys(baselineData).length > 0;
    const hasBaseline2 = Object.keys(baseline2Data).length > 0;

    let data = dashboardData['month_array'].map((month) => {
      let entry = {
        name: month,
        month: month,
        [SELECTION_KEY]: 0,
        [SELECTION_BASELINE_KEY]: 0,
        [SELECTION_BASELINE2_KEY]: 0,
      };
      selectedUtilities.forEach((utility) => {
        entry[utility] = 0
        if (hasBaseline) {
          entry[utility + ' Baseline'] = 0
        }
        if (hasBaseline2) {
          entry[utility + ' Baseline 2'] = 0
        }
      })

      return entry
    })

    data.forEach((entry, index) => {
      activeIds.forEach((id) => {
        selectedUtilities.forEach((utility) => {
          let local_utility = utility.toLowerCase()
          let val = getVal(dashboardData, id, local_utility, chartType, index, selectedOtherTypes)
          entry[utility] += val
          entry[SELECTION_KEY] += val
          if (hasBaseline) {
            let baselineVal = getVal(baselineData, id, local_utility, chartType, index, selectedOtherTypes)
            entry[utility + ' Baseline'] += baselineVal
            entry[SELECTION_BASELINE_KEY] += baselineVal

          }
          if (hasBaseline2) {
            let baseline2Val = getVal(baseline2Data, id, local_utility, chartType, index, selectedOtherTypes)
            entry[utility + ' Baseline 2'] += baseline2Val
            entry[SELECTION_BASELINE2_KEY] += baseline2Val
          }

        })
      })
    })
    return data
  }, [])

  useEffect(() => {
    setCostData(convertToChartForm(dashboardData, baselineData, baseline2Data, selectedUtilities, 'cost', activeIds, selectedOtherTypes))
    setUsageData(convertToChartForm(dashboardData, baselineData, baseline2Data, usageSelectedUtilities, 'usage', activeIds, selectedOtherTypes))
    setGHGData(convertToChartForm(dashboardData, baselineData, baseline2Data, usageSelectedUtilities, 'ghg', activeIds, selectedOtherTypes))

  }, [
    dashboardData, baselineData, baseline2Data, selectedUtilities, usageSelectedUtilities, activeIds, selectedOtherTypes, convertToChartForm
  ])


  return <>
    <Form style={{ marginTop: '25px', marginBottom: '10px' }}>
      <Form.Label style={{ display: 'inline-block', position: 'relative', 'bottom': '3px' }}>Utilities:</Form.Label>
      <Form.Row style={{ marginLeft: '0.5em', display: 'inline-block' }}>
        {availableUtilities.sort().map((utility) => (
          <Checkbox
            style={{
              fontSize: '1.25em',
              marginBottom: '0.5em',
            }}
            color={'primary-o'}
            onChange={() => {
              changeCheck(utility);
            }}
            checked={selectedUtilities.includes(utility)}
            key={utility.charAt(0).toUpperCase() +
              utility.substr(1).toLowerCase()}
          >
            <div>
              <div style={{
                fontSize: '1rem',
                position: 'relative',
                top: '-0.25em',
              }}> {utility.charAt(0).toUpperCase() +
                utility.substr(1).toLowerCase()}</div>
            </div>
          </Checkbox>
        ))}
      </Form.Row>
    </Form>
    {availableOtherTypes.length > 0 &&
      <Form style={{ marginTop: '25px', marginBottom: '10px' }}>
        <Form.Label style={{ display: 'inline-block', position: 'relative', 'bottom': '3px' }}>Other-Types:</Form.Label>
        <Form.Row style={{ marginLeft: '0.5em', display: 'inline-block' }}>
          {availableOtherTypes.sort().map((other_type) => (
            <Checkbox
              style={{
                fontSize: '1.25em',
                marginBottom: '0.5em',
              }}
              color={'primary-o'}
              onChange={() => {
                changeOtherCheck(other_type);
              }}
              checked={selectedOtherTypes.includes(other_type)}
              key={other_type}
            >
              <div>
                <div style={{
                  fontSize: '1rem',
                  position: 'relative',
                  top: '-0.25em',
                }}> {other_type}</div>
              </div>
            </Checkbox>
          ))}
        </Form.Row>
      </Form>}
    <h5>{'Total Cost ($)'} </h5>
    <div style={{ ...row, marginBottom: '4em' }}>
      <EnergyTracerMultiLineChart
        data={!costData ? [] : costData}
        unit={'$'}
        keys={selectedUtilities}
        baseline={Object.keys(baselineData).length > 0 && showBaselineData}
        baseline2={Object.keys(baseline2Data).length > 0 && showBaseline2Data}
        showSummary={costSummary}
      />
      <GraphActionContainer>
        <ChartToggle
          onClick={() => {
            setCostSummary((prev) => !prev);
          }}
          state={costSummary ? 1 : 0}
          title={'Toggle Selection Total'}
          variant={3}
        />
        <ChartDownload
          organization_id={organization.id}
          data={costData}
          headers={chartDownloadHeaders}
          keys={chartDownloadKeys}
          chart_type={'line'}
        />
      </GraphActionContainer>
    </div>
    <h5>{'Total Usage (kBtu)'} </h5>
    <div style={{ ...row, marginBottom: '4em' }}>
      <EnergyTracerMultiLineChart
        data={!usageData ? [] : usageData}
        unit={'kbtu'}
        keys={usageSelectedUtilities}
        baseline={Object.keys(baselineData).length > 0 && showBaselineData}
        baseline2={Object.keys(baseline2Data).length > 0 && showBaseline2Data}
        showSummary={usageSummary}
      />
      <GraphActionContainer>
        <ChartToggle
          onClick={() => {
            setUsageSummary((prev) => !prev);
          }}
          state={usageSummary ? 1 : 0}
          title={'Toggle Selection Total'}
          variant={3}
        />
        <ChartDownload
          organization_id={organization.id}
          data={usageData}
          headers={usageChartDownloadHeaders}
          keys={usageChartDownloadKeys}
          chart_type={'line'}
        />
      </GraphActionContainer>
    </div>
    {activeGHGLicense &&
      <>
        <h5>
          {'Total Emissions'}

          <Form.Group controlId={'ghg-unit-select'} style={{ display: 'inline-block', marginLeft: '1em' }}>
            <Form.Control
              as='select'
              onChange={(e) => setActiveGHGUnit(e.target.value)}
              defaultValue={activeGHGUnit}
            >
              {Object.keys(ghg_units).map((unit) => (
                <option key={`unit-opt-${unit}`} value={unit}>
                  {ghg_units[unit]}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
        </h5>
        <div style={{ ...row, marginBottom: '4em' }}>
          <EnergyTracerMultiLineChart
            data={!convertedGHGData ? [] : convertedGHGData}
            unit={activeGHGUnit}
            keys={usageSelectedUtilities}
            baseline={Object.keys(baselineData).length > 0 && showBaselineData}
            baseline2={Object.keys(baseline2Data).length > 0 && showBaseline2Data}
            showSummary={GHGSummary}
          />
          <GraphActionContainer>
            <ChartToggle
              onClick={() => {
                setGHGSummary((prev) => !prev);
              }}
              state={GHGSummary ? 1 : 0}
              title={'Toggle Selection Total'}
              variant={3}
            />
            <ChartDownload
              organization_id={organization.id}
              data={convertedGHGData}
              headers={usageChartDownloadHeaders}
              keys={usageChartDownloadKeys}
              chart_type={'line'}
            />
          </GraphActionContainer>
        </div></>}
  </>

}

UtilityBreakdown.propTypes = {
  baselineData: PropTypes.object,
  baseline2Data: PropTypes.object,

};

UtilityBreakdown.defaultProps = {
  baselineData: {},
  baseline2Data: {},
};

export default UtilityBreakdown;


function getVal(dataSet, id, local_utility, chartType, index, otherTypes) {
  if (local_utility !== 'other') {
    let val = dataSet['building_usage'][id] &&
      dataSet['building_usage'][id][local_utility]
      ? dataSet['building_usage'][id][local_utility][chartType][
      index
      ]
      : 0
    if (chartType === 'usage') {
      val = convertUsage(val, local_utility)
    }
    return val
  }
  if (local_utility === 'other') {
    let val = 0
    if (dataSet['building_usage'][id] && dataSet['building_usage'][id]['other']) {
      Object.keys(dataSet['building_usage'][id]['other']['sub_costs']).forEach((sub) => {
        if (otherTypes.includes(sub)) {
          val += dataSet['building_usage'][id]['other']['sub_costs'][sub][index]
        }
      })
    }
    return val
  }

}

function convertUsage(val, utility) {
  if (utility === 'electric') {
    return val * 3.412142450123
  }
  if (utility === 'gas') {
    return val * 102
  }
  if (utility === 'propane') {
    return val * 91.5
  }
  if (utility === 'water') {
    return 0 //water not converted to usage
  }
  // if unsupported utility
  return val
}